import React, { useState, useEffect } from "react";
import { 
  FaArrowLeft, FaEnvelope, FaPhone, FaCalendar, FaMapMarkerAlt, 
  FaUser, FaLink, FaCheck, FaTimes, FaInstagram, FaYoutube, 
  FaTiktok, FaFacebook, FaTwitter, FaGlobe, FaIdCard, FaClock,
  FaChartBar, FaCheckCircle, FaHourglassHalf, FaBan, FaStar,
  FaCrown, FaGem, FaUsers, FaEye, FaHeart, FaComment, FaShare,
  FaExternalLinkAlt, FaUserCheck, FaUserPlus, FaUserFriends,
  FaThumbsUp, FaPlay, FaImage, FaVideo, FaCalendarAlt,
  FaMapPin, FaInfoCircle, FaShieldAlt, FaWallet, FaCreditCard
} from 'react-icons/fa';
import { 
  MdVerified, MdPending, MdCancel, MdAnalytics, MdSubscriptions,
  MdVisibility, MdFavorite, MdShare, MdComment, MdRefresh,
  MdLink, MdOutlineSocialDistance, MdOutlinePeopleAlt
} from 'react-icons/md';
import { useUserDetails } from '../../hooks/useAdmin.ts';
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";

// Types
interface SocialAccount {
  id: string;
  platform: string;
  url: string;
  handle_or_username: string;
  followers: number;
  following: number;
  total_posts: number;
  avg_likes: number;
  highest_likes: number;
  last_fetched_at: string | null;
}

interface ProfileLinks {
  [key: string]: {
    link: string;
    status: string;
  };
}

interface InfluencerData {
  id: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  others: Record<string, any>;
  interests: string[];
  district: string;
  state: string;
  pincode: string;
  profile_links: ProfileLinks;
  profile: string;
}

interface VerificationInfo {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requested_at: string;
  admin_comments: string | null;
}

interface Stats {
  applied: number;
  accepted: number;
  completed: number;
  rejected: number;
}

interface Subscription {
  id: string;
  subscription: string;
  start_date: string;
  end_date: string;
  is_expired: boolean;
  is_active: boolean;
  limit_balance: number;
}

interface Profile {
  first_name: string;
  last_name: string | null;
  phone_number: string | null;
  date_of_birth: string | null;
  gender: string | null;
  profile_picture: string | null;
  profile_picture_variants?: {
    small?: string;
    medium?: string;
    large?: string;
  };
}

interface UserDetails {
  id: string;
  email: string;
  role: string;
  profile: Profile;
  verification_info: VerificationInfo | null;
  influencer_data: InfluencerData | null;
  stats: Stats;
  social_accounts: SocialAccount[];
  subscription: Subscription | null;
}

const ViewDocsIn: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [influencerData, setInfluencerData] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'social' | 'stats' | 'subscription'>('overview');

  // Extract influencerId from URL
  const influencerId = window.location.pathname.split('/').pop() || '';

  const { data: response, isLoading: hookLoading, error: hookError } = useUserDetails(influencerId);

  useEffect(() => {
    if (response) {
      setInfluencerData(response);
      setLoading(false);
    }
    if (hookError) {
      setError((hookError as any)?.response?.data?.message || "Failed to fetch influencer data");
      setLoading(false);
    }
    if (hookLoading === false && !response && !hookError) {
      setLoading(false);
    }
  }, [response, hookLoading, hookError]);

  const renderPlatformIcon = (platform: string): JSX.Element => {
    const p = platform.toLowerCase();
    if (p.includes('instagram')) return <FaInstagram className="text-pink-500" size={20} />;
    if (p.includes('youtube')) return <FaYoutube className="text-red-500" size={20} />;
    if (p.includes('tiktok')) return <FaTiktok className="text-black dark:text-white" size={20} />;
    if (p.includes('facebook')) return <FaFacebook className="text-blue-600" size={20} />;
    if (p.includes('twitter')) return <FaTwitter className="text-blue-400" size={20} />;
    return <FaGlobe className="text-gray-500" size={20} />;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getFullImageUrl = (imagePath: string | null): string => {
    if (!imagePath) return "/default-avatar.png";
    if (imagePath.startsWith('http')) return imagePath;
    const baseUrl = process.env.REACT_APP_PF || '';
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    return `${baseUrl}/${cleanPath}`;
  };

  const getVerificationBadge = (status: string | undefined) => {
    switch (status) {
      case 'APPROVED':
        return {
          icon: <MdVerified className="text-blue-500" size={20} />,
          text: 'Verified',
          color: 'text-blue-600',
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          gradient: 'from-blue-500 to-blue-600'
        };
      case 'PENDING':
        return {
          icon: <MdPending className="text-yellow-500" size={20} />,
          text: 'Pending Verification',
          color: 'text-yellow-600',
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          gradient: 'from-yellow-500 to-yellow-600'
        };
      case 'REJECTED':
        return {
          icon: <MdCancel className="text-red-500" size={20} />,
          text: 'Rejected',
          color: 'text-red-600',
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          gradient: 'from-red-500 to-red-600'
        };
      default:
        return {
          icon: <FaHourglassHalf className="text-gray-500" size={20} />,
          text: 'Not Verified',
          color: 'text-gray-600',
          bg: 'bg-gray-50 dark:bg-gray-900/20',
          border: 'border-gray-200 dark:border-gray-800',
          gradient: 'from-gray-500 to-gray-600'
        };
    }
  };

  const getSubscriptionStatus = (subscription: Subscription | null) => {
    if (!subscription) return null;
    
    return {
      isActive: subscription.is_active && !subscription.is_expired,
      badge: subscription.is_active && !subscription.is_expired 
        ? { text: 'Active', color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' }
        : { text: 'Expired', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20' }
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: currentTheme.background }}>
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 bg-blue-500/20 rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-6 text-lg font-medium" style={{ color: currentTheme.text }}>Loading Profile...</p>
          <p className="text-sm mt-2" style={{ color: currentTheme.textSecondary }}>Please wait while we fetch the data</p>
        </div>
      </div>
    );
  }

  if (error || !influencerData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: currentTheme.background }}>
        <div className="max-w-md w-full text-center p-8 rounded-2xl shadow-2xl" style={{ backgroundColor: currentTheme.surface }}>
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <FaBan className="text-4xl text-red-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: currentTheme.text }}>Oops! Something went wrong</h2>
          <p className="mb-8" style={{ color: currentTheme.textSecondary }}>{error || 'Unable to load influencer profile'}</p>
          <button
            onClick={() => window.history.back()}
            className="px-8 py-3 rounded-xl font-medium transition-all hover:scale-105 shadow-lg flex items-center gap-2 mx-auto"
            style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
          >
            <FaArrowLeft size={16} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const verificationBadge = getVerificationBadge(influencerData.verification_info?.status);
  const subscriptionStatus = getSubscriptionStatus(influencerData.subscription);

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: currentTheme.background }}>
      <div className="max-w-7xl mx-auto">
        {/* Header with Navigation */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl md:text-4xl font-bold" style={{ color: currentTheme.text }}>
                Influencer Profile
              </h1>
              <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${verificationBadge.bg} ${verificationBadge.border} border`}>
                {verificationBadge.icon}
                <span style={{ color: verificationBadge.color }}>{verificationBadge.text}</span>
              </span>
            </div>
            <p className="text-sm flex items-center gap-2" style={{ color: currentTheme.textSecondary }}>
              <FaIdCard size={14} />
              ID: {influencerData.id}
            </p>
          </div>
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all hover:scale-105 shadow-lg"
            style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
          >
            <FaArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>

        {/* Profile Header Card */}
        <div className="rounded-2xl shadow-2xl overflow-hidden mb-8 transition-all hover:shadow-3xl" 
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          
          {/* Cover Photo with Gradient */}
          <div className={`h-40 bg-gradient-to-r ${verificationBadge.gradient} relative`}>
            <div className="absolute -bottom-16 left-8">
              <div className="relative group">
                <div className="w-32 h-32 rounded-2xl border-4 overflow-hidden shadow-xl transition-transform group-hover:scale-105"
                     style={{ borderColor: currentTheme.surface, backgroundColor: currentTheme.card }}>
                  {influencerData.profile.profile_picture && !imageError ? (
                    <img
                      src={getFullImageUrl(influencerData.profile.profile_picture)}
                      alt={`${influencerData.profile.first_name}`}
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <FaUser className="text-5xl text-gray-400" />
                    </div>
                  )}
                </div>
                {influencerData.verification_info?.status === 'APPROVED' && (
                  <div className="absolute -bottom-2 -right-2 bg-blue-500 rounded-full p-1.5 border-2 border-white shadow-lg">
                    <MdVerified className="text-white" size={16} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Info */}
          <div className="pt-20 p-8">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h2 className="text-3xl font-bold" style={{ color: currentTheme.text }}>
                    {influencerData.profile.first_name} {influencerData.profile.last_name || ''}
                  </h2>
                  {influencerData.profile.gender && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      {influencerData.profile.gender}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  <InfoChip 
                    icon={<FaEnvelope />}
                    label="Email"
                    value={influencerData.email}
                    theme={currentTheme}
                  />
                  <InfoChip 
                    icon={<FaPhone />}
                    label="Phone"
                    value={influencerData.profile.phone_number?.includes('Error') ? 'Encrypted' : influencerData.profile.phone_number || 'Not provided'}
                    theme={currentTheme}
                  />
                  {influencerData.profile.date_of_birth && (
                    <InfoChip 
                      icon={<FaCalendar />}
                      label="Date of Birth"
                      value={formatDate(influencerData.profile.date_of_birth)}
                      theme={currentTheme}
                    />
                  )}
                </div>
              </div>

              {/* Subscription Card */}
              {influencerData.subscription && (
                <div className="lg:w-80 w-full">
                  <div className={`p-5 rounded-xl border-2 ${subscriptionStatus?.isActive ? 'border-green-500 bg-green-50/50' : 'border-gray-300 bg-gray-50/50'}`}
                       style={{ backgroundColor: currentTheme.card }}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold flex items-center gap-2" style={{ color: currentTheme.text }}>
                        <FaCrown className="text-yellow-500" />
                        Subscription
                      </h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${subscriptionStatus?.isActive ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>
                        {subscriptionStatus?.isActive ? 'Active' : 'Expired'}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: currentTheme.textSecondary }}>Plan ID:</span>
                        <span className="font-mono" style={{ color: currentTheme.text }}>{influencerData.subscription.subscription.substring(0, 8)}...</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: currentTheme.textSecondary }}>Start Date:</span>
                        <span style={{ color: currentTheme.text }}>{formatDate(influencerData.subscription.start_date)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: currentTheme.textSecondary }}>End Date:</span>
                        <span style={{ color: currentTheme.text }}>{formatDate(influencerData.subscription.end_date)}</span>
                      </div>
                      <div className="flex justify-between font-medium pt-2 border-t" style={{ borderColor: currentTheme.border }}>
                        <span style={{ color: currentTheme.textSecondary }}>Limit Balance:</span>
                        <span className="text-green-600">₹{influencerData.subscription.limit_balance.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6 border-b pb-2" style={{ borderColor: currentTheme.border }}>
          {(['overview', 'social', 'stats', 'subscription'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-t-xl font-medium capitalize transition-all relative ${
                activeTab === tab 
                  ? 'text-white shadow-lg' 
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              style={activeTab === tab ? { backgroundColor: currentTheme.primary } : {}}
            >
              {tab}
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"></div>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Personal Info & Location */}
              <div className="lg:col-span-1 space-y-6">
                <Card title="Personal Details" icon={<FaUser />} theme={currentTheme}>
                  <div className="space-y-4">
                    <InfoRow 
                      icon={<FaUser />}
                      label="Full Name"
                      value={`${influencerData.profile.first_name} ${influencerData.profile.last_name || ''}`}
                      theme={currentTheme}
                    />
                    <InfoRow 
                      icon={<FaEnvelope />}
                      label="Email"
                      value={influencerData.email}
                      theme={currentTheme}
                    />
                    <InfoRow 
                      icon={<FaPhone />}
                      label="Phone"
                      value={influencerData.profile.phone_number?.includes('Error') ? 'Encrypted' : influencerData.profile.phone_number || 'Not provided'}
                      theme={currentTheme}
                    />
                    <InfoRow 
                      icon={<FaCalendar />}
                      label="Date of Birth"
                      value={influencerData.profile.date_of_birth ? formatDate(influencerData.profile.date_of_birth) : 'Not provided'}
                      theme={currentTheme}
                    />
                    <InfoRow 
                      icon={<FaUserCheck />}
                      label="Gender"
                      value={influencerData.profile.gender || 'Not specified'}
                      theme={currentTheme}
                    />
                  </div>
                </Card>

                {/* Location Card */}
                {influencerData.influencer_data && (
                  <Card title="Location" icon={<FaMapMarkerAlt />} theme={currentTheme}>
                    <div className="space-y-4">
                      <InfoRow 
                        icon={<FaMapPin />}
                        label="District"
                        value={influencerData.influencer_data.district || 'Not provided'}
                        theme={currentTheme}
                      />
                      <InfoRow 
                        icon={<FaMapMarkerAlt />}
                        label="State"
                        value={influencerData.influencer_data.state || 'Not provided'}
                        theme={currentTheme}
                      />
                      <InfoRow 
                        icon={<FaIdCard />}
                        label="Pincode"
                        value={influencerData.influencer_data.pincode || 'Not provided'}
                        theme={currentTheme}
                      />
                    </div>
                  </Card>
                )}
              </div>

              {/* Right Column - Interests & Profile Links */}
              <div className="lg:col-span-2 space-y-6">
                {/* Interests */}
                {influencerData.influencer_data?.interests && influencerData.influencer_data.interests.length > 0 && (
                  <Card title="Interests & Hobbies" icon={<FaHeart />} theme={currentTheme}>
                    <div className="flex flex-wrap gap-2">
                      {influencerData.influencer_data.interests.map((interest, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 rounded-xl text-sm font-medium transition-all hover:scale-105"
                          style={{ 
                            backgroundColor: `${currentTheme.primary}15`,
                            color: currentTheme.primary,
                            border: `1px solid ${currentTheme.primary}30`
                          }}
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Profile Links */}
                {influencerData.influencer_data?.profile_links && Object.keys(influencerData.influencer_data.profile_links).length > 0 && (
                  <Card title="Profile Links" icon={<FaLink />} theme={currentTheme}>
                    <div className="space-y-3">
                      {Object.entries(influencerData.influencer_data.profile_links).map(([platform, data], index) => (
                        <a
                          key={index}
                          href={data.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-4 rounded-xl transition-all hover:scale-[1.02] hover:shadow-lg group"
                          style={{ 
                            backgroundColor: currentTheme.surface,
                            border: `1px solid ${currentTheme.border}`,
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {renderPlatformIcon(platform)}
                            <div>
                              <p className="font-medium capitalize" style={{ color: currentTheme.text }}>
                                {platform}
                              </p>
                              <p className="text-xs" style={{ color: currentTheme.textSecondary }}>
                                Status: {data.status}
                              </p>
                            </div>
                          </div>
                          <FaExternalLinkAlt 
                            size={14} 
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ color: currentTheme.primary }}
                          />
                        </a>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Account Timeline */}
                {influencerData.influencer_data && (
                  <Card title="Account Timeline" icon={<FaClock />} theme={currentTheme}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InfoRow 
                        icon={<FaCalendarAlt />}
                        label="Created At"
                        value={formatDate(influencerData.influencer_data.created_at)}
                        theme={currentTheme}
                      />
                      <InfoRow 
                        icon={<FaClock />}
                        label="Last Updated"
                        value={formatDate(influencerData.influencer_data.updated_at)}
                        theme={currentTheme}
                      />
                    </div>
                  </Card>
                )}
              </div>
            </div>
          )}

          {/* Social Accounts Tab */}
          {activeTab === 'social' && (
            <div className="space-y-6">
              <Card title="Connected Social Media Accounts" icon={<FaUsers />} theme={currentTheme}>
                {influencerData.social_accounts.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {influencerData.social_accounts.map((account, index) => (
                      <div
                        key={index}
                        className="group relative overflow-hidden rounded-xl transition-all hover:shadow-2xl hover:scale-[1.02]"
                        style={{ 
                          backgroundColor: currentTheme.surface,
                          border: `1px solid ${currentTheme.border}`,
                        }}
                      >
                        {/* Platform Header */}
                        <div className="p-6 border-b" style={{ borderColor: currentTheme.border }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-3 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                                {renderPlatformIcon(account.platform)}
                              </div>
                              <div>
                                <h3 className="font-semibold capitalize text-lg" style={{ color: currentTheme.text }}>
                                  {account.platform}
                                </h3>
                                <a 
                                  href={account.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm hover:underline flex items-center gap-1"
                                  style={{ color: currentTheme.primary }}
                                >
                                  @{account.handle_or_username}
                                  <FaExternalLinkAlt size={12} />
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="p-6">
                          <div className="grid grid-cols-2 gap-4">
                            <SocialStat 
                              icon={<FaUsers />}
                              label="Followers"
                              value={formatNumber(account.followers)}
                              color="text-blue-500"
                              theme={currentTheme}
                            />
                            <SocialStat 
                              icon={<FaUserFriends />}
                              label="Following"
                              value={formatNumber(account.following)}
                              color="text-green-500"
                              theme={currentTheme}
                            />
                            <SocialStat 
                              icon={<FaHeart />}
                              label="Avg Likes"
                              value={formatNumber(account.avg_likes)}
                              color="text-red-500"
                              theme={currentTheme}
                            />
                            <SocialStat 
                              icon={<FaStar />}
                              label="Highest Likes"
                              value={formatNumber(account.highest_likes)}
                              color="text-yellow-500"
                              theme={currentTheme}
                            />
                          </div>

                          <div className="mt-4 pt-4 border-t flex justify-between items-center" style={{ borderColor: currentTheme.border }}>
                            <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
                              <FaPlay className="inline mr-1" size={12} />
                              {account.total_posts} Posts
                            </span>
                            {account.last_fetched_at && (
                              <span className="text-xs" style={{ color: currentTheme.textSecondary }}>
                                Updated: {new Date(account.last_fetched_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState 
                    icon={<FaUsers className="text-5xl" />}
                    title="No Social Accounts"
                    message="This influencer hasn't connected any social media accounts yet."
                    theme={currentTheme}
                  />
                )}
              </Card>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div className="space-y-6">
              {/* Campaign Stats */}
              <Card title="Campaign Performance" icon={<MdAnalytics />} theme={currentTheme}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <StatCard
                    title="Applied"
                    value={influencerData.stats.applied}
                    icon={<FaHourglassHalf />}
                    color="text-blue-500"
                    bg="bg-blue-50 dark:bg-blue-900/20"
                    theme={currentTheme}
                  />
                  <StatCard
                    title="Accepted"
                    value={influencerData.stats.accepted}
                    icon={<FaCheckCircle />}
                    color="text-green-500"
                    bg="bg-green-50 dark:bg-green-900/20"
                    theme={currentTheme}
                  />
                  <StatCard
                    title="Completed"
                    value={influencerData.stats.completed}
                    icon={<FaStar />}
                    color="text-purple-500"
                    bg="bg-purple-50 dark:bg-purple-900/20"
                    theme={currentTheme}
                  />
                  <StatCard
                    title="Rejected"
                    value={influencerData.stats.rejected}
                    icon={<FaBan />}
                    color="text-red-500"
                    bg="bg-red-50 dark:bg-red-900/20"
                    theme={currentTheme}
                  />
                </div>
              </Card>

              {/* Success Rate */}
              <Card title="Success Rate Analysis" icon={<FaChartBar />} theme={currentTheme}>
                <div className="p-6">
                  <div className="flex items-center gap-6">
                    <div className="flex-1">
                      <div className="h-6 rounded-full overflow-hidden flex" style={{ backgroundColor: currentTheme.border }}>
                        <div 
                          className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all"
                          style={{ 
                            width: `${influencerData.stats.applied > 0 
                              ? (influencerData.stats.completed / influencerData.stats.applied) * 100 
                              : 0}%` 
                          }}
                        />
                        <div 
                          className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 transition-all"
                          style={{ 
                            width: `${influencerData.stats.applied > 0 
                              ? (influencerData.stats.accepted / influencerData.stats.applied) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                      <div className="flex justify-between mt-2 text-xs">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                          Completed: {((influencerData.stats.completed / (influencerData.stats.applied || 1)) * 100).toFixed(1)}%
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                          Accepted: {((influencerData.stats.accepted / (influencerData.stats.applied || 1)) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="text-center px-6 py-4 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}10` }}>
                      <p className="text-3xl font-bold" style={{ color: currentTheme.primary }}>
                        {influencerData.stats.applied > 0 
                          ? ((influencerData.stats.completed / influencerData.stats.applied) * 100).toFixed(1) 
                          : 0}%
                      </p>
                      <p className="text-xs mt-1" style={{ color: currentTheme.textSecondary }}>Completion Rate</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && influencerData.subscription && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Subscription Details */}
              <Card title="Subscription Details" icon={<MdSubscriptions />} theme={currentTheme}>
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
                    <div>
                      <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Status</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`w-2 h-2 rounded-full ${subscriptionStatus?.isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        <span className="font-medium" style={{ color: currentTheme.text }}>
                          {subscriptionStatus?.isActive ? 'Active' : 'Expired'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Subscription ID</p>
                      <p className="font-mono text-sm mt-1" style={{ color: currentTheme.text }}>
                        {influencerData.subscription.id.substring(0, 12)}...
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow 
                      icon={<FaCalendar />}
                      label="Start Date"
                      value={formatDate(influencerData.subscription.start_date)}
                      theme={currentTheme}
                    />
                    <InfoRow 
                      icon={<FaCalendarAlt />}
                      label="End Date"
                      value={formatDate(influencerData.subscription.end_date)}
                      theme={currentTheme}
                    />
                  </div>

                  <div className="p-4 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}10` }}>
                    <p className="text-sm mb-2" style={{ color: currentTheme.textSecondary }}>Plan Details</p>
                    <div className="flex items-center justify-between">
                      <span className="font-medium" style={{ color: currentTheme.text }}>Limit Balance</span>
                      <span className="text-2xl font-bold text-green-600">₹{influencerData.subscription.limit_balance.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Usage Stats */}
              <Card title="Subscription Usage" icon={<FaChartBar />} theme={currentTheme}>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span style={{ color: currentTheme.textSecondary }}>Balance Used</span>
                      <span className="font-medium" style={{ color: currentTheme.text }}>0%</span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: currentTheme.border }}>
                      <div className="h-full w-0 bg-gradient-to-r from-green-400 to-green-500 rounded-full"></div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="p-4 text-center rounded-xl" style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
                      <FaWallet className="mx-auto mb-2 text-2xl" style={{ color: currentTheme.primary }} />
                      <p className="text-2xl font-bold" style={{ color: currentTheme.text }}>
                        ₹{influencerData.subscription.limit_balance.toLocaleString()}
                      </p>
                      <p className="text-xs mt-1" style={{ color: currentTheme.textSecondary }}>Available Balance</p>
                    </div>
                    
                    <div className="p-4 text-center rounded-xl" style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
                      <FaCreditCard className="mx-auto mb-2 text-2xl" style={{ color: currentTheme.primary }} />
                      <p className="text-2xl font-bold" style={{ color: currentTheme.text }}>
                        {Math.ceil((new Date(influencerData.subscription.end_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))}d
                      </p>
                      <p className="text-xs mt-1" style={{ color: currentTheme.textSecondary }}>Days Remaining</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper Components
interface CardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  theme: any;
}

const Card: React.FC<CardProps> = ({ title, icon, children, theme }) => (
  <div className="rounded-2xl shadow-xl overflow-hidden" style={{ backgroundColor: theme.card }}>
    <div className="p-6 border-b" style={{ borderColor: theme.border, backgroundColor: `${theme.primary}15` }}>
      <h3 className="font-semibold flex items-center gap-2 text-lg" style={{ color: theme.primary }}>
        {icon}
        {title}
      </h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  theme: any;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value, theme }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme.primary}10`, color: theme.primary }}>
      {icon}
    </div>
    <div>
      <p className="text-xs" style={{ color: theme.textSecondary }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: theme.text }}>{value}</p>
    </div>
  </div>
);

interface InfoChipProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  theme: any;
}

const InfoChip: React.FC<InfoChipProps> = ({ icon, label, value, theme }) => (
  <div className="flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: `${theme.primary}5`, border: `1px solid ${theme.border}` }}>
    <div className="text-sm" style={{ color: theme.primary }}>{icon}</div>
    <div>
      <p className="text-xs" style={{ color: theme.textSecondary }}>{label}</p>
      <p className="text-sm font-medium truncate max-w-[200px]" style={{ color: theme.text }}>{value}</p>
    </div>
  </div>
);

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bg: string;
  theme: any;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, bg, theme }) => (
  <div className="rounded-xl p-6 transition-all hover:shadow-lg hover:scale-105" 
       style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}` }}>
    <div className="flex items-center justify-between mb-4">
      <div className={`p-3 rounded-xl ${bg}`}>
        <div className={color}>{icon}</div>
      </div>
      <span className="text-3xl font-bold" style={{ color: theme.text }}>{value}</span>
    </div>
    <p className="text-sm" style={{ color: theme.textSecondary }}>{title}</p>
  </div>
);

interface SocialStatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
  theme: any;
}

const SocialStat: React.FC<SocialStatProps> = ({ icon, label, value, color, theme }) => (
  <div className="text-center p-3 rounded-lg" style={{ backgroundColor: `${theme.primary}5` }}>
    <div className={`${color} mb-1`}>{icon}</div>
    <p className="text-lg font-bold" style={{ color: theme.text }}>{value}</p>
    <p className="text-xs" style={{ color: theme.textSecondary }}>{label}</p>
  </div>
);

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  message: string;
  theme: any;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, message, theme }) => (
  <div className="text-center py-16">
    <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
      <div style={{ color: theme.textSecondary }}>{icon}</div>
    </div>
    <h4 className="text-xl font-medium mb-3" style={{ color: theme.text }}>{title}</h4>
    <p style={{ color: theme.textSecondary }}>{message}</p>
  </div>
);

export default ViewDocsIn;